import Box from '@mui/material/Box';
import MuiModal from '@mui/material/Modal';
import type { ReactNode } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ICON_CLOSE } from 'Config/Icons';

import styles from './Modal.module.css';
import { ModalHeader } from './ModalHeader/ModalHeader';

interface ModalBaseProps {
    open: boolean; // TODO: Remove this? I don't think we ever pass in `false`.

    header?: string;
    subHeader?: string;
}

interface ModalLoadingProps extends ModalBaseProps {
    isLoading: true;
    /** Used as the onClick of a "Close" button rendered alongside the Placeholder loading animation, so the user doesn't get stuck if loading never finishes. */
    closeModal: () => void;

    children?: never;
    /** If defined, this is displayed in place of the loading animation. Pass in a value if loading data for the content of the modal fails. */
    failureMessage?: string;
}

interface ModalLoadedProps extends ModalBaseProps {
    isLoading?: false;
    children: ReactNode;
}

export type ModalProps = ModalLoadingProps | ModalLoadedProps;

/**
 * Renders a modal in the center of the screen.
 * If data needs to be loaded before the content of the modal can be displayed, pass in `isLoading`, `closeModal`, and `failureMessage` (if there was a failure loading data).
 *
 * TODO: Many (most? all?) parents pass success and failure messages as props.children elements here. Move those to string props instead?
 */
export const Modal = (props: ModalProps) => {
    const { open, header, subHeader } = props;

    return (
        <MuiModal open={open} role="dialog" aria-modal="true">
            <Box
                sx={{
                    // Center the modal vertically and horizontally within the viewport.
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',

                    // Under ideal circumstances, the modal is 800px wide. If the viewport is too narrow to support this without the modal overflowing horizontally, the modal becomes 90% the width of the viewport.
                    width: 'min(800px, 90vw)',

                    // Under ideal circumstances, the modal is as tall as its content. If the viewport is too short to support this without the modal overflowing vertically (either because the user's browser window is small, or because the modal has a lot of content), the modal becomes 95% the height of the viewport and the modal becomes scrollable.
                    maxHeight: '95vh',
                    overflowY: 'auto',

                    // Other styling.
                    bgcolor: 'var(--hps-white)',
                    boxShadow: 24,
                    p: 3, // This results in padding of 24px, as this is a multiplier for the default theme's 8px of padding. Let it be known that I think this is a terrible name for a prop.
                    borderRadius: 1,
                }}
            >
                {header && subHeader && <ModalHeader text={header} secondaryText={subHeader} />}
                {header && !subHeader && <ModalHeader text={header} />}
                {props.isLoading ? <ModalLoadingContent closeModal={props.closeModal} failureMessage={props.failureMessage} /> : props.children}
            </Box>
        </MuiModal>
    );
};

interface ModalLoadingContentProps {
    closeModal: () => void;

    failureMessage?: string;
}

const ModalLoadingContent = (props: ModalLoadingContentProps) => {
    return (
        <>
            {props.failureMessage && <Alert type="error">{props.failureMessage}</Alert>}
            <Box component="form" noValidate>
                {props.failureMessage === undefined && (
                    <div className={styles.loadingPlaceholder}>
                        <Placeholder />
                    </div>
                )}
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.closeModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                </div>
            </Box>
        </>
    );
};
